import axios from 'axios'
import {
  FALLBACK_IMAGE,
  buildProductDescription,
  buildProductJsonLd,
  buildBreadcrumbJsonLd
} from '~/utils/seo/jsonLd'
import { load as cacheLoad } from '~/utils/seo/ssrCache'

// SSR API 호출 타임아웃 (raw axios 기본 0=무한 → 3초 가드로 hang 방지)
const SSR_API_TIMEOUT = { timeout: 3000 }

/**
 * TTL+LRU 캐시 + single-flight로 백엔드 API 호출 흡수. 고시/상세정보는 잘 안 바뀌어 크롤러 버스트에 효과적.
 * - 성공 응답만 캐시 (loader throw 시 cacheLoad가 캐시하지 않음 → 다음 요청에서 재시도).
 * - TTL 만료 직후 동시 요청은 single-flight로 1회 호출로 병합 (스탬피드 방지).
 * - resp 전체가 아닌 { data }만 캐시 (메모리 절감 + request 참조 누수 방지).
 *   호출측은 기존 resp.data 접근 패턴 그대로 호환.
 */
function cachedPost (endpoint, pdNo, ttlMs) {
  const key = endpoint + ':' + pdNo
  return cacheLoad(key, ttlMs, async () => {
    const { data } = await axios.post(process.env.API_BASE_URL + endpoint, { pdNo }, SSR_API_TIMEOUT)
    return { data }
  })
}

/**
 * 상품상세 페이지 seoMeta 핸들러
 * - 기존 seoMeta.js의 상품상세 로직을 분리
 * - buildProductDescription()으로 자연어 description 생성
 */
export default async function handleProduct ({ store, route }) {
  const { pdNo } = route.query
  if (!pdNo) { return }

  const canonicalUrl = `https://www.daisomall.co.kr/pd/pdr/SCR_PDR_0001?pdNo=${pdNo}`

  try {
    // TTL 캐시로 백엔드 부하 흡수: 고시 10분 / 상세 5분 / 기본정보 60초.
    // 크롤러 버스트 시 동일 상품 반복 요청은 캐시 히트 → API 호출 급감.
    // - selPdDtlInfo: 실패 시 전체 폴백(기존 동작) — .catch 없이 throw 전파
    // - selPdDtlDesc/Ntfc: 실패 시 null(기존 동작) — 고시정보만 생략
    const [{ data: res }, descResult, ntfcResult] = await Promise.all([
      cachedPost('/pd/pdr/pdDtl/selPdDtlInfo', pdNo, 60 * 1000),
      cachedPost('/pd/pdr/pdDtl/selPdDtlDesc', pdNo, 5 * 60 * 1000).catch(() => null),
      cachedPost('/pd/pdr/pdDtl/selPdDtlNtfc', pdNo, 10 * 60 * 1000).catch(() => null)
    ])

    if (!res.success || !res.data) {
      store.commit('seoMeta/set', {
        title: '다이소몰',
        description: '온라인 국민가게, 다이소',
        ogUrl: canonicalUrl,
        ogImage: FALLBACK_IMAGE,
        canonical: canonicalUrl
      })
      return
    }

    const pd = res.data
    const pdName = pd.exhPdNm || ''
    const brand = pd.brndNm || '다이소'
    const ctgr = pd.exhCtgr && pd.exhCtgr.length > 0 ? pd.exhCtgr[0] : null
    const categoryName = ctgr ? (ctgr.sctgrNm || ctgr.mctgrNm || ctgr.lctgrNm || '') : ''
    const price = pd.pdPrc || ''

    // 상세설명 텍스트 추출
    let detailText = ''
    const descRes = descResult && descResult.data
    if (descRes && descRes.success && descRes.data && descRes.data.pdDtlDesc && descRes.data.pdDtlDesc.vsipPdDtl) {
      detailText = descRes.data.pdDtlDesc.vsipPdDtl
        .replace(/<[^>]+>/g, '')
        .replace(/&[^;]+;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // 재고 상태
    const availability = (pd.sleStsCd === '03' && pd.exhYn === 'Y' && pd.onldOpt && pd.onldOpt.some(opt => opt.stckQy > 0))
      ? 'InStock'
      : 'OutOfStock'

    // 자연어 description 생성
    const description = buildProductDescription({
      name: pdName,
      brand,
      category: categoryName,
      price,
      detailText,
      rating: pd.revwCnt > 0 ? { avg: pd.revwAvg, count: pd.revwCnt } : null,
      availability
    })

    // 타이틀용 간략 설명
    const shortDesc = [pdName, brand, categoryName, price ? `${Number(price).toLocaleString()}원` : ''].filter(Boolean).join(' | ')

    // 이미지 URL
    const imageUrl = pd.imgUrl
      ? (pd.imgUrl.startsWith('http') ? pd.imgUrl : `https://cdn.daisomall.co.kr${pd.imgUrl}`)
      : FALLBACK_IMAGE

    // HTML 엔티티 디코딩 (SSR 환경 — DOM 불가, 정규식 기반)
    const ENTITIES = { middot: '·', lsquo: '\u2018', rsquo: '\u2019', ldquo: '\u201C', rdquo: '\u201D', ndash: '\u2013', mdash: '\u2014', hellip: '\u2026', bull: '\u2022', nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }
    const decodeEntities = (s) => {
      return (s || '').replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(+d)).replace(/&(\w+);/g, (m, n) => ENTITIES[n] || m)
    }

    // 고시정보 → additionalProperty + manufacturer (AEO·GEO)
    let attributes = null
    let manufacturer = null
    const ntfcRes = ntfcResult && ntfcResult.data
    if (ntfcRes && ntfcRes.success && Array.isArray(ntfcRes.data) && ntfcRes.data.length > 0) {
      attributes = ntfcRes.data
        .filter((item) => {
          const v = (item.ntfcIemCn || '').trim()
          return v && v !== '상세페이지 참조' && v !== '-'
        })
        .map(item => ({
          name: decodeEntities((item.ntfcIemNm || '').trim()),
          value: decodeEntities((item.ntfcIemCn || '').trim())
        }))
      // 제조사 추출: 일반 '제조자'/'제조사' + 화장품 '제조업자' 라벨 모두 허용
      // (값이 '상세페이지 참조'면 아래 가드에서 제외됨)
      const mfrItem = ntfcRes.data.find(item => /제조[자사]|제조업자/.test(item.ntfcIemNm || ''))
      if (mfrItem && mfrItem.ntfcIemCn && mfrItem.ntfcIemCn.trim() !== '상세페이지 참조') {
        manufacturer = decodeEntities(mfrItem.ntfcIemCn.trim())
      }
    }

    // Product JSON-LD
    const product = buildProductJsonLd({
      name: pdName,
      image: imageUrl,
      description,
      sku: pdNo,
      brand,
      manufacturer,
      url: canonicalUrl,
      price,
      availability,
      category: categoryName,
      rating: pd.revwCnt > 0 ? { avg: pd.revwAvg, count: pd.revwCnt } : null,
      attributes
    })

    // BreadcrumbList JSON-LD
    const breadcrumbItems = [{ name: '홈', url: 'https://www.daisomall.co.kr' }]
    if (ctgr) {
      if (ctgr.lctgrNm) { breadcrumbItems.push({ name: ctgr.lctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.lctgrNo || ''}` }) }
      if (ctgr.mctgrNm) { breadcrumbItems.push({ name: ctgr.mctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.mctgrNo || ''}` }) }
      if (ctgr.sctgrNm) { breadcrumbItems.push({ name: ctgr.sctgrNm, url: `https://www.daisomall.co.kr/ds/exbt/${ctgr.sctgrNo || ''}` }) }
    }
    breadcrumbItems.push({ name: pdName, url: canonicalUrl })
    const breadcrumb = buildBreadcrumbJsonLd(breadcrumbItems)

    store.commit('seoMeta/set', {
      title: shortDesc ? `${shortDesc} - 다이소몰` : '다이소몰',
      description,
      ogUrl: canonicalUrl,
      ogImage: imageUrl,
      ogType: 'product',
      canonical: canonicalUrl,
      mobileUrl: `https://m.daisomall.co.kr/pd/pdl/SCR_PDL_0001?pdNo=${pdNo}`,
      h1: pdName,
      jsonLd: [product, breadcrumb]
    })
  } catch (e) {
    console.error('[seoMeta/product]', e.message)
    store.commit('seoMeta/set', {
      title: '다이소몰',
      description: '온라인 국민가게, 다이소',
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      canonical: canonicalUrl
    })
  }
}
